import { useState, useMemo, useEffect, Suspense, startTransition } from "react";
import {
  privateAgent,
  Button,
  DialogBox,
  TextButton,
  AlertDialog,
  useTitle,
} from "../../../lib";
import { DataTable } from "../../../lib/Forms/DataTable";
import { MRT_ColumnDef } from "material-react-table";
import { Formik, Form } from "formik";
import { useQuery, useQueryClient } from "react-query";
import { routesName } from "../../../lib/RoutesName/Routes";
import { useSnackbar } from "notistack";
import AddBehaviorFields from "../../../components/formFields/Behaviors/AddBehaviorFields";
import { AddBehaviorProps, BehaviorProps } from "../../../lib/Types/BehaviorProps";
import {
  behaviorInitialValues,
  editBehaviorIntitialValues,
} from "../../../components/formFields/Behaviors/behavior-helpers";
import AddBehaviorValidationSchema from "../../../components/formFields/Behaviors/AddBehaviorValidationSchema";
import BehaviorSearchFields, {
  defaultSearchParams,
  SearchParams,
} from "../../../components/formFields/Behaviors/BehaviorSearchFields";
import {
  ButtonContainer,
  TopContainerNoMargin,
  UsersContainer,
  TableContainer,
} from "src/styles/Containers";
import { TitleLarge } from "src/styles/Typography";
import { useBehaviors } from "@lib/Hooks/behavior/behavior.hook";
import PaigeSkeleton from "@components/formFields/skeleton/PaigeSkeleton";
import HoverMenu, { BehaviorMenuOptions, CommandContext, DeleteCommand, DuplicateCommand, EditCommand } from "@components/shared/HoverMenu";

function BehaviorPage() {
  useTitle("Behaviors | Paige");
  const [openAddBehaiorDialog, setOpenAddBehaviorDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [behaviorId, setBehaviorId] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [fieldInitialValues, setFieldInitialValues] =
    useState<AddBehaviorProps>(behaviorInitialValues as AddBehaviorProps);

  const { isLoading, data, filteredData, handleSearchQuery,deleteBehaviorMutation, updateBehaviorMutation, addBehaviorMutation } = useBehaviors();


  const handleSubmit = async (values: AddBehaviorProps) => {
      if (isEdit) {
        await updateBehaviorMutation.mutateAsync({
          id: behaviorId,
          data: values
        })
        setOpenAddBehaviorDialog(false);
      } else {
        await addBehaviorMutation.mutateAsync({
          id: behaviorId,
          data: values
        })
        setOpenAddBehaviorDialog(false);
      }
      
  };

  const handleDelete = async () => {
    setOpenDeleteDialog(false);
    await deleteBehaviorMutation.mutateAsync(behaviorId)
  };

  const handleRowClick = (column: any, row: any) => {
    if (column.id === "name") {
      startTransition(()=>{
        setOpenAddBehaviorDialog(true);
        setFieldInitialValues(editBehaviorIntitialValues(row.original));
        setBehaviorId(row.original.id);
        setIsEdit(true);
      })
    }
  };


  //grid defn
  const columns = useMemo<MRT_ColumnDef<BehaviorProps>[]>(
    () => [
      {
        accessorFn: (row) => `${row.name}`,
        id: "name",
        header: "Name",
      },
      {
        accessorFn: (row) => `${row.behaviorCategory?.name}`,
        id: "behaviorCategory",
        header: "Behavior Category",
      },
      {
        id: "actions",
        header: "Actions",
        headerClassName: "header-row",
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableBodyCellProps: {
          align: "right",
          className: "action",
        },
        size: 50,
        enableResizing: false, //disable resizing for this column
        Cell: ({ cell }) => {
          const context: CommandContext<BehaviorProps> = {
            setOpenDialog: setOpenAddBehaviorDialog,
            setFieldInitialValues,
            setId: setBehaviorId,
            setIsEdit,
            row: cell.row.original,
          };

          const commands = {
            edit: new EditCommand(context),
            duplicate: new DuplicateCommand(context),
            delete: new DeleteCommand({
              ...context,
              setOpenDialog: setOpenDeleteDialog,
            }),
          };

          return <HoverMenu id={0} commands={commands} options={BehaviorMenuOptions} />
        },
      },
    ],
    []
  );
  //end of grid defn

  

  return (
    <>
      <AlertDialog
        variety='error'
        onConfirm={() => handleDelete()}
        onClose={() => setOpenDeleteDialog(false)}
        isOpen={openDeleteDialog}
        title={"Are you sure you want to delete this behavior?"}
        content={
          "Deleting this behavior will permanently erase any data associated with this behavior."
        }
        btnText={{ cancel: "Cancel", action: "Delete" }}
      />
      <DialogBox
        isOpen={openAddBehaiorDialog}
        onClose={() => setOpenAddBehaviorDialog(false)}
        title={isEdit ? "Edit Behavior" : "Add Behavior"}
        content={
          <Formik
            initialValues={fieldInitialValues}
            onSubmit={handleSubmit}
            validationSchema={AddBehaviorValidationSchema}
          >
            {(formikProps) => (
              <Form>
                <AddBehaviorFields />
                <ButtonContainer>
                  <TextButton
                    label={"Cancel"}
                    onClick={() => setOpenAddBehaviorDialog(false)}
                  ></TextButton>
                  <Button
                    label={"Save"}
                    type={"submit"}
                    disabled={!formikProps.isValid}
                    loading={addBehaviorMutation.isLoading || updateBehaviorMutation.isLoading}
                  ></Button>
                </ButtonContainer>
              </Form>
            )}
          </Formik>
        }
      />
      <TopContainerNoMargin>
        <TitleLarge>Behaviors</TitleLarge>
        <Button
          label={"+ Add Behavior"}
          onClick={() => {
            startTransition(()=>{
              setOpenAddBehaviorDialog(true);
              setIsEdit(false);
              setFieldInitialValues(behaviorInitialValues);
            })
            
          }}
        ></Button>
      </TopContainerNoMargin>
      <UsersContainer>
        <BehaviorSearchFields
          onSearchQuery={(searchQuery: SearchParams) =>
            handleSearchQuery(searchQuery)
          }
        />
        <Suspense fallback={<PaigeSkeleton/>}>
          <TableContainer>
            {filteredData && filteredData.length > 0 && (
              <DataTable
                columns={columns}
                data={filteredData}
                onCellClick={handleRowClick}
                enableSorting={true}
                enableRowOrdering={false}
              />
            )}
          </TableContainer>
        </Suspense>
      </UsersContainer>
    </>
  );
}

export default BehaviorPage;
